import { useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';
import { useTenantApi } from '@/tenant/useTenant';
import { useToast } from '@/components/useToast';

export interface WaiveDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  statementId: number;
}

const FIELDS = ['reason'] as const;

export function WaiveDialog(props: WaiveDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <WaiveForm key={useFreshOnOpen(props.open)} {...props} />;
}

function WaiveForm({ open, onClose, onDone, statementId }: WaiveDialogProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const api = useTenantApi();
  const describe = useActionError();
  const reasonRef = useRef<HTMLInputElement>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { reason: reasonRef }, submitting);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = reason.trim();
    if (trimmed === '') {
      setError(null);
      fields.fail({ reason: t('validation.reasonWaive') });
      return;
    }
    setSubmitting(true);
    setError(null);
    fields.reset();
    try {
      await api.waiveStatement(statementId, { reason: trimmed });
      showToast(t('subscribers.toast.waived'));
      onDone();
      onClose();
    } catch (err) {
      const action = describe(err);
      setError(action);
      fields.failMarked(mapFieldErrors(action.fieldKeys, { reason: 'reason' }), {
        reason: t('subscribers.field.reason'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose}>
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>{t('subscribers.waive.title')}</DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <TextField
            required
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            label={t('subscribers.waive.reason')}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              fields.clear('reason');
            }}
            inputRef={reasonRef}
            error={fields.errors.reason !== undefined}
            helperText={fields.errors.reason}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={submitting}>
            {t('subscribers.actions.waive')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
